import { ActiveOrder } from 'app/page/orders/model/active-order';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { BillingAccountUtils } from 'app/page/orders/utils/billing-account-utils';
import { DateUtils } from 'app/utils/date-utils';

export class OrderUtils {
    private static readonly PARAM_SERVICE_POINT_ID: string = 'SERVICE_POINT_ID';

    public static getShoppingCart(activeOrder: ActiveOrder): OrderTrackerBackend.ShoppingCart {
        return (
            activeOrder.orderSummary?.shoppingCart ||
            (() => {
                throw new Error(`Cannot find shopping cart for order ${activeOrder.order.id}`);
            })()
        );
    }

    public static findAppointment(activeOrder: ActiveOrder): OrderTrackerBackend.Appointment | undefined {
        return activeOrder.orderSummary?.appointment;
    }

    public static getAppointment(activeOrder: ActiveOrder): OrderTrackerBackend.Appointment {
        return (
            OrderUtils.findAppointment(activeOrder) ||
            (() => {
                throw new Error(`Cannot find appointment for order ${activeOrder.order.id}`);
            })()
        );
    }

    public static hasAppointment(activeOrder: ActiveOrder): boolean {
        return !!OrderUtils.findAppointment(activeOrder);
    }

    public static getServicePointId(activeOrder: ActiveOrder): string {
        return OrderUtils.getShoppingCart(activeOrder).parameters[OrderUtils.PARAM_SERVICE_POINT_ID];
    }

    public static getActivationDate(activeOrder: ActiveOrder): string {
        return (
            OrderUtils.getShoppingCart(activeOrder).summary?.targetDate ||
            (() => {
                throw new Error(`Cannot find activation date for order ${activeOrder.order.id}`);
            })()
        );
    }

    public static isOrderChangeable(activeOrder: ActiveOrder): boolean {
        return !!activeOrder.orderSummary?.changeableOrder;
    }

    public static canChangeActivationDate(activeOrder: ActiveOrder): boolean {
        const activationDate = OrderUtils.getActivationDate(activeOrder);

        return OrderUtils.isOrderChangeable(activeOrder) && DateUtils.isDateInTheFuture(activationDate);
    }

    public static getActiveOrder(
        billingAccounts: BillingAccount[],
        billingAccountId: string,
        orderId: string,
    ): ActiveOrder {
        const billingAccount = BillingAccountUtils.getBillingAccount(billingAccounts, billingAccountId);

        return billingAccount.activeOrders[orderId];
    }

    public static getOrderSummary(
        billingAccounts: BillingAccount[],
        billingAccountId: string,
        orderId: string,
    ): OrderTrackerBackend.OrderSummary | undefined {
        return OrderUtils.getActiveOrder(billingAccounts, billingAccountId, orderId).orderSummary;
    }

    public static getLeadTime(billingAccounts: BillingAccount[], billingAccountId: string): number {
        const billingAccount = BillingAccountUtils.getBillingAccount(billingAccounts, billingAccountId);
        const { activeOrders, selectedOrderId } = billingAccount;

        if (!selectedOrderId) {
            throw new Error('Order must be selected.');
        }

        return activeOrders[selectedOrderId].order.serviceAddress?.servicePoints?.[0].details?.leadTime || 0;
    }
}
